import React, { useEffect, useState } from 'react';
import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormLabel from '@c/forms/controls/FormLabel';
import BaseModal from '@c/modals/BaseModal';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@ui/Button';
import { ProductDocument } from 'models/product';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { formatCurrency, isMobile } from '@util/index';
import PriceDropIcon from './assets/price-drop-2.svg';
import Image from 'next/image';
import { LightBulbIcon } from '@heroicons/react/20/solid';
import { logError } from '@util/logError';
import MobileDrawer from '@c/drawers/MobileDrawer';
import SafeImage from '@ui/SafeImage';

const BASE_PERCENTAGE = 15;

export const getNewPrice = (
  productPrice: string | number | undefined,
  percentage: number
) => {
  return (
    Number(productPrice) -
    (Number(productPrice) * percentage) / 100
  ).toFixed(2);
};

export default function PriceDropModal({
  open,
  setOpen,
  product,
  setPromoteModalIsOpen,
  updatePriceDrop,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPromoteModalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  product: ProductDocument;
  updatePriceDrop: (
    newPrice: number,
    product: ProductDocument
  ) => Promise<void> | void;
}) {
  const priceDropSchema = z.object({
    price: z
      .number({
        required_error: 'Price is required',
        invalid_type_error: 'Please enter a valid price',
      })
      .gte(1, 'The new price cannot be lower than $1')
      .refine(
        (value) => value < product.price * 0.95,
        `The new price must be lower than ${formatCurrency(
          product.price * 0.95
        )}`
      ),
  });

  type PriceDropModalFormData = z.infer<typeof priceDropSchema>;

  const [selectedPercentage, setSelectedPercentage] = useState(BASE_PERCENTAGE);
  const [loading, setLoading] = useState(false);

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    reset,
  } = useForm<z.infer<typeof priceDropSchema>>({
    resolver: zodResolver(priceDropSchema),
    defaultValues: {
      price: product ? Number(getNewPrice(product.price, 15)) : 0,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        price: product
          ? Number(getNewPrice(product.price, BASE_PERCENTAGE))
          : 0,
      });
      setSelectedPercentage(BASE_PERCENTAGE);
    }
  }, [open, product, reset]);

  const onConfirmPriceDrop = async (data: PriceDropModalFormData) => {
    try {
      setLoading(true);
      await updatePriceDrop(data.price ?? 0, product);
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const _isMobile = isMobile();
  const Base = _isMobile ? MobileDrawer : BaseModal;

  const BackButton = () => {
    if (!_isMobile) return null;

    return (
      <button
        className="text-body-md font-semibold text-text-red"
        onClick={() => {
          setOpen(false);
          if (setPromoteModalIsOpen) {
            setPromoteModalIsOpen(true);
          }
        }}
      >
        Back
      </button>
    );
  };

  return (
    <Base
      show={open}
      title="Promote your listing"
      isOpen={open}
      dismiss={() => setOpen(false)}
      headerExtraButton={<BackButton />}
    >
      <div className="flex w-full flex-col p-8">
        <div className="mb-10 flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <Image
              src={PriceDropIcon}
              alt="smart pricing icon"
              width={56}
              height={56}
            />
            <span className="text-heading-h5 font-bold">Price Drop</span>
          </div>
          <span className="text-body-md font-medium">
            Decrease the price of your product and automatically put it on sale.
          </span>
        </div>

        <div className="mb-6 flex w-full items-start gap-5 rounded-xl bg-brand-off-white p-4">
          <SafeImage
            src={product.thumbnail}
            alt={product.title}
            width={56}
            height={56}
            className="aspect-square size-28 rounded-2xl object-cover"
          />
          <div className="flex flex-col gap-2">
            <span className="text-body-md font-semibold">{product.title}</span>
            <span className="text-body-md font-medium text-text-muted">
              Current Price: {formatCurrency(product.price)}
            </span>
          </div>
        </div>

        <div className="mb-6 grid w-full grid-cols-3 gap-x-4">
          {[10, 15, 20].map((percentage) => (
            <div
              className={`rounded-3xl border p-8 ${
                selectedPercentage === percentage
                  ? 'border-brand-secondary bg-[#f9e8e8]'
                  : 'border-[#d9d9d9]  hover:border-brand-secondary'
              } flex cursor-pointer items-center justify-center gap-x-2 text-[1.8rem]`}
              onClick={() => {
                setValue(
                  'price',
                  Number(getNewPrice(product.price, percentage))
                );
                setSelectedPercentage(percentage);
              }}
              key={percentage}
            >
              <div className="flex flex-col items-center">
                <span className="text-heading-h6 font-bold">
                  {formatCurrency(
                    Number(getNewPrice(product.price, percentage))
                  )}
                </span>

                <span className="text-body-md font-semibold text-text-red">
                  {percentage}% OFF
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-6 flex items-center justify-center gap-8">
          <div className="h-px w-full bg-brand-lighter-gray" />
          <span className="text-body-md font-semibold">or</span>
          <div className="h-px w-full bg-brand-lighter-gray" />
        </div>

        <form onSubmit={handleSubmit(onConfirmPriceDrop)}>
          <div className="mb-16 flex w-full justify-center">
            <FormLabel
              value="Set new price"
              required
              errorMessage={errors.price?.message}
            >
              <Controller
                control={control}
                name={'price'}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="flex w-full justify-center">
                    <div className="w-full">
                      <FormCurrencyInputFancy
                        placeholder={'0.00'}
                        error={!!errors?.price}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value?.toString() ?? ''}
                        allowEmpty
                      />
                    </div>
                  </div>
                )}
              />
            </FormLabel>
          </div>

          {product.floor_price && product.price !== product.floor_price && (
            <div className="mb-8 flex w-full items-start gap-x-4 rounded-lg bg-red-100 px-12 py-8 text-black">
              <LightBulbIcon className="h-14 w-14 text-red-500" />

              <h3 className="max-w-[45rem] text-2xl">
                You currently have Smart Pricing enabled. Adjusting the price
                will disable Smart Pricing.
              </h3>
            </div>
          )}

          <Button
            text={loading ? 'Loading...' : 'Confirm'}
            type="secondary"
            className="w-full"
            buttonType="submit"
          />
        </form>
      </div>
    </Base>
  );
}
